/**
 * Shared API Client
 *
 * Centralized API configuration and utilities.
 * All modules should use this client for consistent API interactions.
 */

import type { ApiResponse, ApiError } from '@/types/global';
import { notify } from './notificationService';

/**
 * Real Error subclass for API failures. The interface ApiError in
 * @/types/global describes the same shape so existing `as ApiError`
 * casts and structural checks (`error.code`, `error.status`,
 * `error.data`) keep working — the difference is that an instance of
 * this class satisfies `error instanceof Error`, so the global error
 * sink (errorReporter window listeners) shows the real message
 * instead of `[object Object]`.
 */
export class ApiClientError extends Error implements ApiError {
  status: number;
  code: string;
  data?: unknown;
  constructor(message: string, status: number, code: string, data?: unknown) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = code;
    this.data = data;
  }
}
import { i18n } from '@/core';
import { authService } from '@/core/auth/authService';
import {
  getImpersonationToken,
  getImpersonationScope,
} from '@/core/auth/impersonation';
import { deriveApiBaseUrl } from './apiBase';
import { reportError } from '@/core/observability/errorReporter';

// ── snake_case → camelCase transformer ────────────────────────────────────────
//
// Both transforms recurse into every object. Some subtrees must be left
// verbatim — foreign payloads (webhook bodies, third-party API responses
// stored for forensics) and maps whose KEYS are data, not field names
// (gateway slug → method maps). Requests can opt out per key with
// `preserveKeys` in RequestConfig: the matching key itself is still
// renamed, but its value subtree is passed through untouched.

/**
 * Flatten a DRF error payload ({detail}, {field: [msgs]}, nested arrays)
 * into a single human-readable sentence. Field keys are already camelCased
 * by the response transformer.
 */
function extractApiErrorMessage(body: unknown, fallback: string): string {
  const flatten = (value: unknown): string => {
    if (value == null) return '';
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) {
      return value.map(flatten).filter(Boolean).join(' ');
    }
    if (typeof value === 'object') {
      const record = value as Record<string, unknown>;
      if (typeof record.detail === 'string') return record.detail;
      return Object.entries(record)
        .map(([key, entry]) => {
          const message = flatten(entry);
          if (!message) return '';
          return key === 'nonFieldErrors' ? message : `${key}: ${message}`;
        })
        .filter(Boolean)
        .join(' · ');
    }
    return '';
  };
  return flatten(body) || fallback;
}

function snakeToCamel(str: string): string {
  return str.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
}

function transformKeys(value: unknown, preserveKeys?: readonly string[]): unknown {
  if (Array.isArray(value)) {
    return value.map((v) => transformKeys(v, preserveKeys));
  }
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([k, v]) => {
        const camelKey = snakeToCamel(k);
        const preserve = preserveKeys?.includes(k) || preserveKeys?.includes(camelKey);
        return [camelKey, preserve ? v : transformKeys(v, preserveKeys)];
      })
    );
  }
  return value;
}

function camelToSnake(str: string): string {
  return str.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
}

function transformKeysToSnake(value: unknown, preserveKeys?: readonly string[]): unknown {
  if (Array.isArray(value)) {
    return value.map((v) => transformKeysToSnake(v, preserveKeys));
  }
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([k, v]) => {
        const snakeKey = camelToSnake(k);
        const preserve = preserveKeys?.includes(k) || preserveKeys?.includes(snakeKey);
        return [snakeKey, preserve ? v : transformKeysToSnake(v, preserveKeys)];
      })
    );
  }
  return value;
}

export const API_BASE_URL = deriveApiBaseUrl();
const IS_NGROK = API_BASE_URL.includes('ngrok');

interface RequestConfig extends RequestInit {
  params?: Record<string, string | number | boolean>;
  skipAuthRedirect?: boolean;
  /** Show toast notifications for errors */
  showErrorToast?: boolean;
  /** Skip automatic location_id injection (use for company-level endpoints) */
  skipLocationFilter?: boolean;
  /**
   * Object keys (camelCase or snake_case) whose VALUE subtrees are excluded
   * from the automatic case transforms, in both directions. Use for foreign
   * payloads kept for forensics (webhook bodies, third-party responses) and
   * for maps whose keys are data rather than field names (gateway slugs).
   * The key itself is still renamed; only its contents are preserved.
   */
  preserveKeys?: readonly string[];
  /**
   * Read the response as a Blob instead of JSON/text. Required for binary
   * downloads (invoice PDFs, exports) — the default text path corrupts them.
   */
  responseType?: 'blob';
}

type ErrorHandler = (error: ApiError) => void;

function isFormData(value: unknown): value is FormData {
  return typeof FormData !== 'undefined' && value instanceof FormData;
}

function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.split('=').slice(1).join('=')) : null;
}

function isUnsafeMethod(method?: string): boolean {
  const normalized = (method ?? 'GET').toUpperCase();
  return !['GET', 'HEAD', 'OPTIONS'].includes(normalized);
}

// ── Cross-origin CSRF token cache ─────────────────────────────────────────
// On cross-origin deployments deck cannot read the csrftoken cookie via
// ``document.cookie`` — the cookie is scoped to the api origin. The
// backend ships the token in the body of /auth/login/, /auth/me/ and
// /auth/refresh/; authService writes it here so unsafe requests can pick
// it up before the fetch fires. Same-origin dev (localhost:8080 ↔
// localhost:8000) still falls back to the cookie reader below.
let cachedCsrfToken: string | null = null;
export function setCachedCsrfToken(token: string | null | undefined): void {
  if (typeof token === 'string' && token.trim()) {
    cachedCsrfToken = token;
  } else if (token === null) {
    cachedCsrfToken = null;
  }
}
class ApiClient {
  private baseURL: string;
  private locationId: string | null = null;
  private errorHandlers: Map<number | 'default', ErrorHandler> = new Map();

  // ── Refresh deduplication ─────────────────────────────────────────────────
  // When multiple parallel requests hit 401 simultaneously, only ONE refresh
  // attempt is made. All others await the same in-flight promise.
  private inflightRefresh: Promise<boolean> | null = null;

  // ── Error toast deduplication ─────────────────────────────────────────────
  // Prevents the same error toast from firing N times when N parallel queries
  // all fail with the same status (e.g. 401 cascade on session expiry).
  private lastErrorKey: string | null = null;
  private lastErrorTime = 0;
  private static ERROR_DEDUP_WINDOW_MS = 2000;

  /**
   * Active location id used to auto-inject `?location_id=` on every GET
   * and to populate `location` FKs in service POST payloads.
   *
   * Since the scope-in-URL refactor (`docs/plan/SCOPE_IN_URL.md`),
   * this value is derived from the URL via `useActiveContext` and
   * pushed here by `TenantContext`. It MUST NOT be set from any
   * other code path — doing so risks reintroducing the activeContext
   * ↔ URL desync bug. The setter is kept as the single sync point.
   */
  setLocationId(id: string | null): void {
    this.locationId = id;
  }

  /**
   * Read the currently active location ID (null when operating in
   * company context). Services call this to populate `location` in
   * POST payloads where the backend serializer requires an explicit
   * FK. The id is URL-derived (see setLocationId).
   */
  getLocationId(): string | null {
    return this.locationId;
  }

  constructor(baseURL: string) {
    this.baseURL = baseURL;
    this.setupDefaultErrorHandlers();
  }

  /**
   * Setup default error handlers for common HTTP errors
   */
  private setupDefaultErrorHandlers(): void {
    // 401 Unauthorized — JWT expired. Refresh has already been attempted
    // and failed by the time we get here (see request() flow). Show a
    // brief toast, clear the cached user, then route to the editorial
    // /session-expired surface with ?return= so the post-login redirect
    // can put the user back where they were.
    //
    // Hard-redirect via window.location.href: the user will need a fresh
    // JWT regardless, so losing React Router state is fine.
    this.errorHandlers.set(401, () => {
      // Don't show toast or redirect if we're already on a public
      // auth-flow surface — that would loop, trample an in-progress
      // login, or spam "session expired" on pages that never had one.
      const currentPath = window.location.pathname;
      const SKIP_PATHS = new Set([
        '/login',
        '/logout',
        '/session-expired',
        '/access-denied',
        '/unsubscribe',
        '/terms',
        '/privacy',
      ]);
      if (SKIP_PATHS.has(currentPath)) return;

      notify.warning(i18n.t('apiErrors.sessionExpired'));
      authService.clearCachedUser();

      const returnTo = encodeURIComponent(
        `${currentPath}${window.location.search}${window.location.hash}`,
      );
      window.location.href = `/session-expired?return=${returnTo}`;
    });

    // 403 Forbidden
    this.errorHandlers.set(403, (error) => {
      notify.error(i18n.t('apiErrors.forbidden'), {
        description: error.message,
      });
    });

    this.errorHandlers.set(404, () => {
      notify.error(i18n.t('apiErrors.notFound'));
    });

    // 500+ Server errors
    this.errorHandlers.set(500, (error) => {
      notify.error(i18n.t('apiErrors.serverError'), {
        description: error.message,
      });
    });

    // Default error handler
    this.errorHandlers.set('default', (error) => {
      notify.error(error.message || i18n.t('apiErrors.unexpected'));
    });
  }

  /**
   * Register a custom error handler for a specific status code
   */
  onError(statusCode: number | 'default', handler: ErrorHandler): void {
    this.errorHandlers.set(statusCode, handler);
  }

  /**
   * Deduplicated refresh — ensures only one refresh attempt is in-flight
   * even when multiple parallel 401s arrive simultaneously.
   */
  private deduplicatedRefresh(): Promise<boolean> {
    if (this.inflightRefresh) return this.inflightRefresh;

    this.inflightRefresh = authService
      .refreshSession()
      .catch(() => false)
      .finally(() => {
        this.inflightRefresh = null;
      });

    return this.inflightRefresh;
  }

  /**
   * Handle API errors based on status code.
   * Deduplicates identical toasts within a short time window so that
   * parallel failures (e.g. 4× 401) only surface a single notification.
   */
  private handleError(error: ApiError, skipAuthRedirect?: boolean): void {
    const status = error.status || 0;

    // Skip auth redirect if specified
    if (skipAuthRedirect && status === 401) {
      return;
    }

    // ── Toast deduplication ─────────────────────────────────────────────
    // When N parallel requests all fail with the same status (e.g. 5×
    // 401 on session expiry), only the FIRST triggers the handler (toast
    // + redirect). Subsequent identical errors within the window are
    // silently swallowed. For 401 the redirect is a hard
    // window.location.href, so suppressing duplicates is safe.
    const now = Date.now();
    const errorKey = `${status}`;
    if (
      errorKey === this.lastErrorKey &&
      now - this.lastErrorTime < ApiClient.ERROR_DEDUP_WINDOW_MS
    ) {
      return;
    }
    this.lastErrorKey = errorKey;
    this.lastErrorTime = now;

    // Find specific handler or use default
    const handler = this.errorHandlers.get(status)
      || (status >= 500 ? this.errorHandlers.get(500) : undefined)
      || this.errorHandlers.get('default');

    if (handler) {
      handler(error);
    }
  }

  /**
   * Build URL with query parameters
   */
  private buildURL(endpoint: string, params?: Record<string, string | number | boolean>): string {
    const url = new URL(endpoint, this.baseURL);
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        url.searchParams.append(key, String(value));
      });
    }

    return url.toString();
  }

  /**
   * Generic request handler with error interception
   */
  private async request<T>(
    endpoint: string,
    config: RequestConfig = {},
    hasRetriedAuth: boolean = false,
  ): Promise<ApiResponse<T>> {
    const {
      params, skipAuthRedirect, showErrorToast = true, preserveKeys, responseType,
      ...fetchConfig
    } = config;
    const url = this.buildURL(endpoint, params);
    const isMultipartBody = isFormData(fetchConfig.body);

    const headers = new Headers(fetchConfig.headers);
    if (!isMultipartBody && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
    if (isMultipartBody) {
      headers.delete('Content-Type');
    }
    if (IS_NGROK && !headers.has('ngrok-skip-browser-warning')) {
      headers.set('ngrok-skip-browser-warning', 'true');
    }
    if (isUnsafeMethod(fetchConfig.method) && !headers.has('X-CSRFToken')) {
      // Cross-origin SPAs (deck on amplifyapp.com → api on
      // api-staging.shaka.tools) cannot read the csrftoken cookie via
      // ``document.cookie`` because the cookie is scoped to the api
      // origin. The backend now also returns the token in the body of
      // /auth/login/, /auth/me/ and /auth/refresh/; we cache it via
      // ``setCachedCsrfToken`` and surface it here. Same-origin dev
      // (``localhost:8080`` ↔ ``localhost:8000``) still falls back to
      // the cookie path.
      const csrfToken = cachedCsrfToken ?? getCookie('csrftoken');
      if (csrfToken) {
        headers.set('X-CSRFToken', csrfToken);
      }
    }

    // ── Impersonation: forward the staff-issued JWT as Bearer + gate writes ──
    const impersonationToken = getImpersonationToken();
    if (impersonationToken) {
      headers.set('Authorization', `Bearer ${impersonationToken}`);
      const scope = getImpersonationScope();
      if (scope === 'read_only' && isUnsafeMethod(fetchConfig.method)) {
        // Reject before hitting the network. Surface a toast so the staff
        // member knows why nothing happened.
        notify.error(
          i18n.t(
            'impersonation.readOnlyBlocked',
            'Read-only impersonation: this action is blocked. End impersonation to make changes.',
          ),
        );
        const error = new Error('IMPERSONATION_READ_ONLY') as Error & {
          status?: number;
          isImpersonationBlock?: boolean;
        };
        error.status = 403;
        error.isImpersonationBlock = true;
        throw error;
      }
    }

    try {
      const response = await fetch(url, {
        ...fetchConfig,
        // When impersonating, omit cookies so the staff-issued Bearer
        // is the sole auth signal. Otherwise a leftover deck cookie
        // (e.g. another tab still logged in as the operator) would
        // shadow the impersonation via CookieJWTAuthentication and the
        // backend would resolve the request as the cookie's user, not
        // the impersonated one.
        credentials: fetchConfig.credentials
          ?? (impersonationToken ? 'omit' : 'include'),
        headers,
      });

      if (!response.ok) {
        if (
          response.status === 401
          && !skipAuthRedirect
          && !hasRetriedAuth
          && !endpoint.includes('/api/v1/auth/login/')
          && !endpoint.includes('/api/v1/auth/refresh/')
          && !endpoint.includes('/api/v1/auth/credential-login/')
        ) {
          try {
            const refreshed = await this.deduplicatedRefresh();
            if (refreshed) {
              return this.request<T>(endpoint, config, true);
            }
          } catch {
            // Fall through to the normal 401 handler.
          }
        }

        // Attempt to parse a JSON body so callers can read structured error
        // payloads (e.g. { available: false, reason: 'not_configured' } from 503).
        let errorBody: unknown;
        const errContentType = response.headers.get('Content-Type') || '';
        if (errContentType.includes('application/json')) {
          try {
            errorBody = transformKeys(await response.json());
          } catch {
            errorBody = undefined;
          }
        }

        // Build a human-readable message from the DRF payload — statusText is
        // empty under HTTP/2, so error toasts showed no reason at all while
        // the actual validation detail sat unused in error.data.
        const error = new ApiClientError(
          extractApiErrorMessage(errorBody, response.statusText || `HTTP ${response.status}`),
          response.status,
          this.getErrorCode(response.status),
          errorBody,
        );

        // A 403 on a background READ (GET) is a permission-scoped fetch the
        // UI should already gate by role — surfacing a scary "You do not have
        // permission" toast for it is noise (e.g. a dashboard/overview panel
        // that fetches data beyond the viewer's role). Suppress the toast for
        // GET 403s; writes (POST/PATCH/DELETE) still notify since those are
        // explicit user actions where the feedback is legitimate.
        const reqMethod = (fetchConfig.method || 'GET').toUpperCase();
        const isSilentForbidden = response.status === 403 && reqMethod === 'GET';

        // Only show notification if caller hasn't opted out
        if (showErrorToast && !isSilentForbidden) {
          this.handleError(error, skipAuthRedirect);
        } else if (!skipAuthRedirect && response.status === 401) {
          // Always honour 401 redirect even when toasts are suppressed
          this.handleError(error, skipAuthRedirect);
        }

        // Tap into the in-house error sink for server errors only.
        // 4xx is usually validation / auth — legitimate UX feedback,
        // not a bug. 5xx is a backend regression worth surfacing in
        // the admin inbox. The reporter's own dedup window keeps the
        // sink from being flooded by a thundering-herd outage.
        if (response.status >= 500) {
          reportError(new Error(`HTTP ${response.status} ${response.statusText}`), {
            source: 'apiClient',
            endpoint,
            status: response.status,
            method: (fetchConfig.method || 'GET').toUpperCase(),
          });
        }

        throw error;
      }

      if (response.status === 204 || response.status === 205) {
        return {
          data: undefined as T,
          status: response.status,
        };
      }

      const contentType = response.headers.get('Content-Type') || '';
      const contentLength = response.headers.get('Content-Length');
      if (contentLength === '0') {
        return {
          data: undefined as T,
          status: response.status,
        };
      }

      // Binary payloads (PDFs, exports) must never go through .text(): the
      // decode corrupts the bytes. Callers opt in with responseType: 'blob'.
      if (responseType === 'blob') {
        return {
          data: (await response.blob()) as T,
          status: response.status,
        };
      }

      if (contentType.includes('application/json')) {
        const raw = await response.json();
        const data = transformKeys(raw, preserveKeys) as T;

        return {
          data,
          status: response.status,
        };
      }

      const text = await response.text();
      return {
        data: (text ? text : undefined) as T,
        status: response.status,
      };
    } catch (error) {
      // Re-throw API errors (already real ApiClientError instances).
      if (error instanceof ApiClientError) {
        throw error;
      }

      // Transport-layer failure — fetch rejected, so there is no HTTP
      // status to reason about. `TypeError: Failed to fetch` (offline,
      // DNS, TLS, CORS), `AbortError` (cancelled request) and a body
      // that fails to parse all land here. Keep the original error's
      // identity in `data.cause`: it used to be flattened into an
      // opaque "Connection error" and the console breadcrumb serialised
      // the Error as `{}`, leaving no way to tell those apart.
      const failedMethod = (fetchConfig.method || 'GET').toUpperCase();
      const cause = error instanceof Error ? error : new Error(String(error));
      console.error(
        `API request failed: ${failedMethod} ${endpoint} — ${cause.name}: ${cause.message}`,
      );

      const networkError = new ApiClientError(
        i18n.t('apiErrors.network'),
        0,
        'NETWORK_ERROR',
        { endpoint, method: failedMethod, cause: `${cause.name}: ${cause.message}` },
      );

      // A write is an explicit user action: failing it silently leaves
      // the operator staring at a form that never saved. Background GETs
      // stay quiet — a blip while polling isn't worth a toast. The status
      // dedup in handleError keeps a full outage down to one notification.
      if (showErrorToast && failedMethod !== 'GET') {
        this.handleError(networkError, skipAuthRedirect);
      }

      // The sink only sees 5xx from the HTTP branch. A write that never
      // reached the server deserves the same visibility — and unlike a
      // 5xx it may have been applied server-side with the response lost.
      if (failedMethod !== 'GET') {
        reportError(cause, {
          source: 'apiClient',
          endpoint,
          status: 0,
          method: failedMethod,
        });
      }

      throw networkError;
    }
  }

  /**
   * Get error code based on HTTP status
   */
  private getErrorCode(status: number): string {
    switch (status) {
      case 400: return 'BAD_REQUEST';
      case 401: return 'UNAUTHORIZED';
      case 403: return 'FORBIDDEN';
      case 404: return 'NOT_FOUND';
      case 422: return 'VALIDATION_ERROR';
      case 429: return 'RATE_LIMITED';
      case 500: return 'SERVER_ERROR';
      case 502: return 'BAD_GATEWAY';
      case 503: return 'SERVICE_UNAVAILABLE';
      default: return 'API_ERROR';
    }
  }

  /**
   * Build the params object with ?location_id= injected when a location
   * context is active. Shared by GET/POST/PUT/PATCH/DELETE so every request
   * the user makes resolves the same tenant-location server-side.
   */
  private buildParams(
    config?: RequestConfig,
  ): Record<string, string | number | boolean> | undefined {
    const inject = this.locationId && !config?.skipLocationFilter;
    if (!inject) return config?.params;
    return { location_id: this.locationId as string, ...config?.params };
  }

  /**
   * GET request — automatically injects ?location_id= when a location context is active.
   * Pass skipLocationFilter: true to bypass injection (e.g. company-level list endpoints).
   */
  async get<T>(endpoint: string, config?: RequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      ...config,
      method: 'GET',
      params: this.buildParams(config),
    });
  }

  /**
   * POST request — same location_id injection as GET so mutations resolve to
   * the intended tenant-location on the server side.
   */
  async post<T>(endpoint: string, body?: unknown, config?: RequestConfig): Promise<ApiResponse<T>> {
    const isMultipartBody = isFormData(body);
    return this.request<T>(endpoint, {
      ...config,
      method: 'POST',
      params: this.buildParams(config),
      body: body === undefined
        ? undefined
        : isMultipartBody
          ? body
          : JSON.stringify(transformKeysToSnake(body, config?.preserveKeys)),
    });
  }

  /**
   * PUT request
   */
  async put<T>(endpoint: string, body?: unknown, config?: RequestConfig): Promise<ApiResponse<T>> {
    const isMultipartBody = isFormData(body);
    return this.request<T>(endpoint, {
      ...config,
      method: 'PUT',
      params: this.buildParams(config),
      body: body === undefined
        ? undefined
        : isMultipartBody
          ? body
          : JSON.stringify(transformKeysToSnake(body, config?.preserveKeys)),
    });
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string, config?: RequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      ...config,
      method: 'DELETE',
      params: this.buildParams(config),
    });
  }

  /**
   * PATCH request
   */
  async patch<T>(endpoint: string, body?: unknown, config?: RequestConfig): Promise<ApiResponse<T>> {
    const isMultipartBody = isFormData(body);
    return this.request<T>(endpoint, {
      ...config,
      method: 'PATCH',
      params: this.buildParams(config),
      body: body === undefined
        ? undefined
        : isMultipartBody
          ? body
          : JSON.stringify(transformKeysToSnake(body, config?.preserveKeys)),
    });
  }
}

// Export singleton instance
export const apiClient = new ApiClient(API_BASE_URL);

/**
 * Walk a DRF page-number-paginated list endpoint and return every row.
 *
 * For catalog-sized sets that services join or filter client-side
 * (products, recipes): a fixed `page_size` alone silently truncates once
 * the catalog outgrows one page. `maxPages` is a runaway backstop, far
 * above any real catalog.
 */
export async function fetchAllPages<T>(
  url: string,
  params: Record<string, string | number | boolean> = {},
  maxPages = 25,
): Promise<T[]> {
  const rows: T[] = [];
  for (let page = 1; page <= maxPages; page++) {
    const response = await apiClient.get<{ results: T[]; next: string | null }>(url, {
      params: { ...params, page_size: 200, page },
    });
    rows.push(...response.data.results);
    if (!response.data.next) break;
  }
  return rows;
}
